import { z } from "zod";
import { adminCollectionParticipantStatusSchema } from "./admin-collection-participants.js";

/**
 * US-AE4.F7-1028 (@UC9952) — trang "Chi tiết" của MỘT suất trong nhóm (admin, chỉ xem): các đợt thu suất đã tham gia + lịch sử đóng
 * tiền. Hai danh sách phân trang con trỏ RIÊNG. Mọi số tiền là string minor units.
 */
export const ADMIN_MEMBER_CONTRIBUTIONS_MAX_LIMIT = 100;

export const adminMemberContributionsQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_MEMBER_CONTRIBUTIONS_MAX_LIMIT).optional(),
  })
  .strict();
export type AdminMemberContributionsQuery = z.infer<typeof adminMemberContributionsQuerySchema>;

const pageInfoSchema = z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict();
const minorSchema = z.string().regex(/^\d+$/);

/**
 * Một dòng = một đợt thu một lần HOẶC một lịch thu (gộp mọi kỳ suất có khoản). Tiền + trạng thái theo CÙNG luật tab Người tham gia của
 * chi tiết đợt (`upcoming` không xuất hiện: dòng chỉ có khi suất đã có khoản). `collectionId` = kỳ mới nhất — link sang chi tiết đợt.
 */
export const adminMemberCollectionItemSchema = z
  .object({
    collectionId: z.string().uuid(),
    title: z.string().nullable(),
    isPeriodic: z.boolean(),
    periodCount: z.number().int().positive(),
    kind: z.string(),
    fundId: z.string().uuid(),
    fundName: z.string(),
    expectedMinor: minorSchema,
    paidMinor: minorSchema,
    outstandingMinor: minorSchema,
    currency: z.string().length(3),
    status: adminCollectionParticipantStatusSchema,
    pendingConfirmationCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type AdminMemberCollectionItem = z.infer<typeof adminMemberCollectionItemSchema>;

/** `memberDisplayName` — tên suất theo luật tên chung (tiêu đề trang). */
export const adminMemberCollectionsResponseSchema = z
  .object({ memberDisplayName: z.string().nullable(), data: z.array(adminMemberCollectionItemSchema), pageInfo: pageInfoSchema })
  .strict();
export type AdminMemberCollectionsResponse = z.infer<typeof adminMemberCollectionsResponseSchema>;

/**
 * Lịch sử đóng tiền: `payment_submitted` = lời khai đã chuyển (`status` chờ xác nhận / đã duyệt / bị từ chối — ba trạng thái P2.1,
 * lời khai KHÔNG phải tiền đã vào sổ) · `payment_recorded` = lần ghi vào sổ (`recorded`, hoặc `reversed` khi đã bị huỷ) ·
 * `payment_reversed` = lần huỷ ghi tiền (`status` null).
 */
export const adminMemberPaymentHistoryKindSchema = z.enum(["payment_submitted", "payment_recorded", "payment_reversed"]);
export type AdminMemberPaymentHistoryKind = z.infer<typeof adminMemberPaymentHistoryKindSchema>;
/** `cancelled` (review #1525): người khai TỰ HUỶ (`rejectReason = PAYMENT_CLAIM_SELF_CANCEL_REASON`) — khác "bị từ chối", không có người duyệt. */
export const adminMemberPaymentHistoryStatusSchema = z.enum(["submitted", "approved", "rejected", "cancelled", "recorded", "reversed"]);
export type AdminMemberPaymentHistoryStatus = z.infer<typeof adminMemberPaymentHistoryStatusSchema>;

export const adminMemberPaymentHistoryItemSchema = z
  .object({
    id: z.string().uuid(),
    kind: adminMemberPaymentHistoryKindSchema,
    status: adminMemberPaymentHistoryStatusSchema.nullable(),
    at: z.string().datetime(),
    amountMinor: minorSchema,
    currency: z.string().length(3),
    collectionId: z.string().uuid(),
    collectionTitle: z.string().nullable(),
    periodKey: z.string().nullable(),
    actorName: z.string().nullable(),
    reviewerName: z.string().nullable(),
    reviewedAt: z.string().datetime().nullable(),
  })
  .strict();
export type AdminMemberPaymentHistoryItem = z.infer<typeof adminMemberPaymentHistoryItemSchema>;

export const adminMemberPaymentHistoryResponseSchema = z
  .object({ data: z.array(adminMemberPaymentHistoryItemSchema), pageInfo: pageInfoSchema })
  .strict();
export type AdminMemberPaymentHistoryResponse = z.infer<typeof adminMemberPaymentHistoryResponseSchema>;
